import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';

// ---------------------------------------------------------------- row shapes

export type DocumentType = 'idea' | 'proposal_idea' | 'meeting_note' | 'note' | 'sop';

// Same vocabulary as the CHECK constraint on documents.entity_type.
export type DocumentEntityType =
  | 'lead'
  | 'deal'
  | 'company'
  | 'contact'
  | 'project'
  | 'task'
  | 'quote'
  | 'invoice';

export interface DocumentRow {
  id: string;
  type: DocumentType;
  title: string;
  body: string;
  entity_type: DocumentEntityType | null;
  entity_id: string | null;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentEdit {
  title: string;
  body: string;
}

// --------------------------------------------------------------- vocabulary

export const DOC_TYPES: DocumentType[] = ['idea', 'proposal_idea', 'meeting_note', 'note', 'sop'];

export const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  idea: 'רעיון',
  proposal_idea: 'הצעה ללקוח',
  meeting_note: 'סיכום פגישה',
  note: 'הערה',
  sop: 'נוהל',
};

export const SOURCE_LABELS: Record<string, string> = {
  'whatsapp-admin': 'נקלט מ-WhatsApp',
  manual: 'נכתב ידנית',
  'meeting-import': 'יובא מפגישה',
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

// --------------------------------------------------------------------- keys

export const documentsKey = (orgId: string | null) => ['documents', orgId] as const;
export const documentEntitiesKey = (orgId: string | null, leadIds: string[], companyIds: string[]) =>
  ['document-entities', orgId, leadIds.join(','), companyIds.join(',')] as const;

// ------------------------------------------------------------------ queries

export function useDocuments(orgId: string | null) {
  return useQuery({
    queryKey: documentsKey(orgId),
    queryFn: async (): Promise<DocumentRow[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('documents')
        .select('id, type, title, body, entity_type, entity_id, source, created_at, updated_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(400)
        // type and entity_type are CHECK constraints the type generator emits
        // as `string`; the rest of the row already comes back correctly typed.
        .overrideTypes<{ type: DocumentType; entity_type: DocumentEntityType | null }[]>();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

/**
 * documents.entity_id is a bare UUID — the table carries no foreign key, because
 * the column points at eight different tables. PostgREST cannot embed it, so the
 * names are resolved with one extra round trip per table, keyed by the ids that
 * are actually on screen.
 */
export function useEntityNames(orgId: string | null, docs: DocumentRow[]) {
  const { leadIds, companyIds } = useMemo(() => {
    const leads = new Set<string>();
    const companies = new Set<string>();
    for (const doc of docs) {
      if (!doc.entity_id) continue;
      if (doc.entity_type === 'lead') leads.add(doc.entity_id);
      if (doc.entity_type === 'company') companies.add(doc.entity_id);
    }
    return {
      leadIds: [...leads].sort(),
      companyIds: [...companies].sort(),
    };
  }, [docs]);

  return useQuery({
    queryKey: documentEntitiesKey(orgId, leadIds, companyIds),
    queryFn: async (): Promise<Record<string, string>> => {
      if (!orgId) return {};
      const names: Record<string, string> = {};

      if (leadIds.length > 0) {
        const { data, error } = await supabase
          .from('leads')
          .select('id, full_name')
          .eq('organization_id', orgId)
          .in('id', leadIds);
        if (error) throw error;
        for (const row of data) names[row.id] = row.full_name;
      }

      if (companyIds.length > 0) {
        const { data, error } = await supabase
          .from('companies')
          .select('id, name')
          .eq('organization_id', orgId)
          .in('id', companyIds);
        if (error) throw error;
        for (const row of data) names[row.id] = row.name;
      }

      return names;
    },
    enabled: Boolean(orgId) && (leadIds.length > 0 || companyIds.length > 0),
    staleTime: 5 * 60 * 1000,
  });
}

/** The record page a linked entity opens, or null when no route exists for it. */
export function entityPath(entityType: DocumentEntityType | null, entityId: string | null): string | null {
  if (!entityType || !entityId) return null;
  if (entityType === 'lead') return `/leads/${entityId}`;
  if (entityType === 'company') return `/customers/${entityId}`;
  if (entityType === 'project') return `/projects/${entityId}`;
  return null;
}

// ---------------------------------------------------------------- mutations

export function useUpdateDocument(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: DocumentEdit }) => {
      const title = values.title.trim();
      if (!title) throw new Error('כותרת חסרה');
      const { error } = await supabase
        .from('documents')
        .update({ title, body: values.body })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: documentsKey(orgId) });
    },
  });
}

export function useSoftDeleteDocument(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    // Soft delete only. The capture pipeline is the sole writer here, so a hard
    // delete would silently discard something the bot recorded from a message
    // that no longer exists anywhere else.
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('documents')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: documentsKey(orgId) });
    },
  });
}
